'use client';

import React from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { buildTrackingId } from '@/utils/tracking-helper';
import Container from '../../shared/Container';
import OptimizedImage from '../../shared/OptimizedImage';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { StaticComparisonTableTile } from './types/StaticComparisonTableTile';
import ComparisonRow from './components/ComparisonRow';
import FeatureRenderer from './components/FeatureRenderer';
import Button from '../../shared/Button';
import { parseProductVariantIntoLink } from '../../shared/ButtonLink/utils/parse-product-variant-into-link';
import Tooltip from './components/Tooltip';

export default function StaticComparisonTable({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: StaticComparisonTableTile;
  trackingPrefix?: string;
}>) {
  const { formatCurrency } = useCurrencyFormatter();
  const { localizedLink } = useLocaleLink();
  const currentPath = usePathname();
  const buttonText = tile['button-text']?.text;
  const products = tile.products?.productVariants;
  const staticProductNames = tile['static-product-names']?.chunks;

  const productsLength = products?.length || staticProductNames?.length || 0;

  return (
    <Container>
      <ComparisonRow productsLength={productsLength}>
        <div className=" hidden sm:block" />
        {products?.map((product, i) => {
          const parsedLink = parseProductVariantIntoLink(product, currentPath, false);
          const productImageTrackingId = buildTrackingId(trackingPrefix, product.name, 'image');
          return (
            <Link
              key={`product-image-${i}`}
              href={localizedLink(parsedLink?.href || '/')}
              className=" mx-auto my-auto h-auto max-h-50 w-20 sm:w-25"
              aria-label={product.name || ''}
              data-tile-element-id={productImageTrackingId}
            >
              <OptimizedImage
                key={`Product image${i}`}
                image={product?.images?.[0]}
                width={150}
                height={150}
                className="object-contain"
                sizes="150px"
              />
            </Link>
          );
        })}
      </ComparisonRow>
      <ComparisonRow productsLength={productsLength}>
        <div className=" hidden sm:block" />
        {products?.map((product, i) => {
          const shortDescription = componentByTypename(
            product.product?.shortDescription?.content,
            'RichTextContent',
          );
          return (
            <div key={`Product description${i}`} className="mb-8 text-center">
              <span className=" mb-2 mt-6 block text-lg font-bold xs:text-xl sm:mt-10 sm:text-2xl">
                {product.name}
              </span>
              <RichTextRenderer blocks={shortDescription} className="text-sm sm:text-base" />
            </div>
          );
        })}
        {staticProductNames?.map((name, i) => {
          const productName = extractComponentFromChunk(name, 'name', 'SingleLineContent');
          return (
            <div key={`Product name${i}`} className="mb-8 text-center">
              <span className="text-lg font-bold xs:text-xl sm:text-2xl">{productName?.text}</span>
            </div>
          );
        })}
      </ComparisonRow>
      {tile.features.chunks.map((feature, idx: number) => {
        const name = extractComponentFromChunk(feature, 'name', 'SingleLineContent')?.text;
        const tooltip = extractComponentFromChunk(feature, 'tooltip', 'PieceContent');

        return (
          <div key={`${idx}comparison-row`} className="px-2 py-3 odd:bg-neutral-5 sm:px-4 sm:py-6">
            <ComparisonRow productsLength={productsLength}>
              {name && (
                <div className="col-span-full flex items-center justify-center gap-2 text-md font-bold xs:text-lg sm:col-span-1 sm:justify-start sm:text-xl">
                  {name}
                  <Tooltip tooltip={tooltip} />
                </div>
              )}
              {Array.from({ length: productsLength }).map((_, index: number) => {
                const featureContent = feature[index + 1]?.content;
                const verifiedFeature = componentByTypename(featureContent, 'RichTextContent');
                return (
                  <div key={`${index}comparison-products`} className="text-center">
                    {/* Adding one to skip the name of the feature */}
                    <FeatureRenderer feature={verifiedFeature} />
                  </div>
                );
              })}
            </ComparisonRow>
          </div>
        );
      })}
      {buttonText && (
        <ComparisonRow productsLength={productsLength}>
          <div className=" hidden sm:block" />
          {products?.map((product, i) => {
            const parsedLink = parseProductVariantIntoLink(product, currentPath, true);
            const buyButtonTrackingId = buildTrackingId(trackingPrefix, product.name, 'buy');
            return (
              <div key={`Product CTA${i}`} className="mt-4 sm:mt-6">
                {product.priceVariant && (
                  <p className=" yellow-underline mx-auto w-fit text-lg font-bold xs:text-xl sm:text-2xl">
                    {formatCurrency(product.priceVariant, {
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    })}
                  </p>
                )}
                <Button
                  href={parsedLink?.href}
                  scroll={parsedLink?.scroll}
                  className="mx-auto mt-4 w-fit sm:mt-6"
                  data-tile-element-id={buyButtonTrackingId}
                >
                  {buttonText}
                </Button>
              </div>
            );
          })}
        </ComparisonRow>
      )}
      <RichTextRenderer
        blocks={tile.disclaimer}
        className="mx-auto mt-8 w-fit text-sm text-neutral-50 sm:mt-16"
      />
    </Container>
  );
}
